import { supabase } from "@/integrations/supabase/client";

interface AdminSession {
  token: string;
  expiresAt: number;
  username: string;
}

const ADMIN_SESSION_KEY = "admin_session";
const SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

/**
 * Generates a cryptographically secure random token
 */
function generateSecureToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Authenticates admin user and creates secure session
 */
export const adminLogin = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Authenticate with database
    const { data: result, error } = await supabase.rpc('admin_login', {
      username_param: username,
      password_param: password
    });

    if (error) {
      console.error("Login error:", error);
      return { success: false, error: "Authentication failed" };
    }

    if (result) {
      // Create secure session
      const session: AdminSession = {
        token: generateSecureToken(),
        expiresAt: Date.now() + SESSION_DURATION,
        username: username
      };

      // Store encrypted session
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
      
      return { success: true };
    } else {
      return { success: false, error: "Invalid username or password" };
    }
  } catch (error) {
    console.error("Login error:", error);
    return { success: false, error: "An error occurred during login" };
  }
};

/**
 * Checks if current admin session is valid
 */
export const isAdminAuthenticated = (): boolean => {
  try {
    const sessionData = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!sessionData) return false;

    const session: AdminSession = JSON.parse(sessionData);
    
    // Check if session has expired
    if (Date.now() > session.expiresAt) {
      localStorage.removeItem(ADMIN_SESSION_KEY);
      return false;
    }

    // Validate token format (basic check)
    if (!session.token || session.token.length !== 64) {
      localStorage.removeItem(ADMIN_SESSION_KEY);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking admin authentication:", error);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    return false;
  }
};

/**
 * Gets current admin session info
 */
export const getAdminSession = (): AdminSession | null => {
  try {
    const sessionData = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!sessionData) return null;

    const session: AdminSession = JSON.parse(sessionData);
    
    if (Date.now() > session.expiresAt) {
      localStorage.removeItem(ADMIN_SESSION_KEY);
      return null;
    }

    return session;
  } catch (error) {
    console.error("Error getting admin session:", error);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    return null;
  }
};

/**
 * Logs out admin user
 */
export const adminLogout = (): void => {
  localStorage.removeItem(ADMIN_SESSION_KEY);
};

/**
 * Extends current session expiration
 */
export const extendAdminSession = (): boolean => {
  try {
    const session = getAdminSession();
    if (!session) return false;

    session.expiresAt = Date.now() + SESSION_DURATION;
    localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
    
    return true;
  } catch (error) {
    console.error("Error extending admin session:", error);
    return false;
  }
};